import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Empty, Input, Modal, Select, Table, Tag } from "antd";
import { Eye, FileText, RefreshCw, Search } from "lucide-react";
import {
  fetchUnprocessedInboundFaxes, classifyInboundFax,
  retryInboundFax, refreshInboundFaxAttachment,
} from "hooks/care/inboundFaxReviewApi";
import { toast } from "react-toastify";

const INCOMING = ["RECEIVED", "STORED", "FAILED"];
const PRESCRIPTIONS = ["PROCESSING", "PENDING_REVIEW"];
const STATUS = {
  RECEIVED: ["Received", "blue"], STORED: ["Ready", "green"],
  FAILED: ["Failed", "red"], PROCESSING: ["Processing", "gold"],
  PENDING_REVIEW: ["Awaiting review", "purple"],
};
const dateTime = value => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
};

export default function InboundFaxManagementPage({ activeTab = "incoming", onNavigate }) {
  const [faxes, setFaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyIds, setBusyIds] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const generation = useRef(0);
  const previewGeneration = useRef(0);
  const pending = useRef(new Set());

  const load = useCallback(async (silent = false) => {
    const request = ++generation.current;
    if (!silent) setLoading(true);
    try {
      const data = await fetchUnprocessedInboundFaxes();
      if (!Array.isArray(data)) throw new Error("The fax queue returned an unexpected response.");
      if (request !== generation.current) return;
      setFaxes(data); setError(null); setUpdatedAt(new Date());
    } catch (err) {
      if (request === generation.current) setError(err?.message || "Could not load incoming faxes.");
    } finally { if (request === generation.current) setLoading(false); }
  }, []);

  useEffect(() => {
    const queueRequests = generation;
    const previewRequests = previewGeneration;
    load();
    const timer = setInterval(() => {
      if (!document.hidden && pending.current.size === 0) load(true);
    }, 30000);
    return () => { clearInterval(timer); queueRequests.current++; previewRequests.current++; };
  }, [load]);
  useEffect(() => { setQuery(""); setStatus("all"); }, [activeTab]);

  const perform = async (fax, action) => {
    if (pending.current.has(fax.id)) return;
    pending.current.add(fax.id); setBusyIds([...pending.current]);
    try { await action(); await load(true); }
    catch (err) { toast.error(err?.message || "The fax could not be updated."); }
    finally { pending.current.delete(fax.id); setBusyIds([...pending.current]); }
  };
  const classify = (fax, isPrescription) => perform(fax, async () => {
    const result = await classifyInboundFax(fax.id, { isPrescription });
    if (!isPrescription) {
      toast.success("Fax marked as non-prescription.");
    } else if (result.status === "PENDING_REVIEW") {
      toast.success("Prescription extracted. Open Review Queue to confirm the resident.");
      onNavigate?.("prescription");
    } else if (result.status === "FAILED") {
      toast.error(`Prescription processing failed: ${result.failureReason || "Check the failure details and retry later."} The fax remains in Incoming.`);
    } else if (result.status === "SKIPPED") {
      toast.warn(result.failureReason || result.routingNotes || "The fax could not be processed as a prescription. No medication drafts were created.");
    } else if (result.status === "PROCESSING") {
      toast.info("Prescription processing is still in progress. Check Prescription List for its status.");
      onNavigate?.("prescription");
    } else {
      toast.warn("Prescription processing did not reach the review stage. Refresh the queue and check the fax status.");
    }
  });
  const retry = fax => perform(fax, async () => {
    const result = await retryInboundFax(fax.id);
    if (result.status === "FAILED") toast.error(result.failureReason || "Document retrieval failed. Please try again later.");
    else toast.success("Fax document retrieved. You can now preview and classify it.");
  });
  const showPreview = async fax => {
    const request = ++previewGeneration.current;
    setPreview(fax); setPreviewError(null); setPreviewLoading(true);
    try {
      const document = fax.storageFileId ? await refreshInboundFaxAttachment(fax.id) : fax;
      if (!document.signedUrl) throw new Error("This fax has no available document. Retry document retrieval from Incoming.");
      if (request === previewGeneration.current) setPreview(document);
    } catch (err) {
      if (request === previewGeneration.current) setPreviewError(err?.message || "Could not open this document.");
    } finally { if (request === previewGeneration.current) setPreviewLoading(false); }
  };

  const incoming = faxes.filter(f => INCOMING.includes(f.status));
  const prescriptions = faxes.filter(f => PRESCRIPTIONS.includes(f.status));
  const failedCount = incoming.filter(f => f.status === "FAILED").length;
  const visible = useMemo(() => {
    const statuses = activeTab === "prescription" ? PRESCRIPTIONS : INCOMING;
    const needle = query.trim().toLowerCase();
    return faxes.filter(f => statuses.includes(f.status)
      && (status === "all" || f.status === status)
      && (!needle || [f.fileName, f.vendorFaxId, f.id, f.fromNumber, f.toNumber, f.routingNotes, f.failureReason]
        .some(value => String(value || "").toLowerCase().includes(needle))));
  }, [faxes, activeTab, status, query]);
  const isPrescription = activeTab === "prescription";
  const total = isPrescription ? prescriptions.length : incoming.length;

  const columns = [
    { title: "Document", key: "document", width: 320, render: (_, fax) => (
      <div className="fax-document-cell">
        <span className="fax-document-icon"><FileText size={20} /></span>
        <div><strong>{fax.fileName || "Unnamed document"}</strong>
          <small>Fax ID: {fax.vendorFaxId || fax.id}</small>
          {fax.failureReason && <details className="fax-failure">
            <summary>View failure details</summary>
            <p>{fax.failureReason}</p>
          </details>}
        </div>
      </div>
    ) },
    { title: "Sender / pages", key: "sender", width: 170, render: (_, fax) => (
      <div><span>{fax.fromNumber || "Unknown sender"}</span>
        <small className="fax-secondary">{fax.pageCount != null ? `${fax.pageCount} page${fax.pageCount === 1 ? "" : "s"}` : "Page count unavailable"}</small>
      </div>
    ) },
    { title: "Received", key: "received", width: 180, render: (_, fax) => dateTime(fax.receivedAt || fax.createdAt) },
    { title: "Status", dataIndex: "status", width: 150, render: value => (
      <Tag color={STATUS[value]?.[1]}>{STATUS[value]?.[0] || value}</Tag>
    ) },
    { title: "Actions", key: "actions", width: 360, render: (_, fax) => {
      const busy = busyIds.includes(fax.id);
      const hasDocument = Boolean(fax.storageFileId || fax.signedUrl);
      return <div className="fax-actions">
        <Button icon={<Eye size={15} />} disabled={busy || !hasDocument} onClick={() => showPreview(fax)}>Preview</Button>
        {INCOMING.includes(fax.status) && <>
          {!hasDocument && ["FAILED", "RECEIVED"].includes(fax.status) ? (
            <Button loading={busy} onClick={() => retry(fax)}>Retry document</Button>
          ) : <Button type="primary" loading={busy} disabled={!hasDocument} onClick={() => classify(fax, true)}>Mark Prescription</Button>}
          <Button disabled={busy} onClick={() => Modal.confirm({
            title: "Mark as non-prescription?", content: "This fax will leave Incoming without creating medication drafts.",
            okText: "Mark non-prescription", onOk: () => classify(fax, false),
          })}>Not Prescription</Button>
        </>}
        {fax.status === "PENDING_REVIEW" && <Button type="primary" onClick={() => onNavigate?.("review")}>Open Review Queue</Button>}
        {fax.status === "PROCESSING" && <small className="fax-secondary">Extraction in progress</small>}
      </div>;
    } },
  ];

  return <div className="fax-management">
    <div className="fax-summary" aria-label="Fax queue summary">
      <div><span>Awaiting classification</span><strong>{incoming.length}</strong></div>
      <div><span>Prescription processing & review</span><strong>{prescriptions.length}</strong></div>
      <div className={failedCount ? "fax-summary-warning" : ""}><span>Failed incoming faxes</span><strong>{failedCount}</strong></div>
    </div>
    {error && <Alert type="error" showIcon title="Could not refresh the fax queue" description={error} action={<Button onClick={() => load()}>Try again</Button>} />}
    {!isPrescription && failedCount > 0 && <Alert type="warning" showIcon title={`${failedCount} fax${failedCount === 1 ? " needs" : "es need"} attention`} description="Failed faxes remain here. Check the failure details and retry any missing documents before classification." />}
    <div className="fax-queue-panel">
      <div className="fax-panel-heading">
        <div><h2>{isPrescription ? "Prescription List" : "Incoming faxes"} <span>{total}</span></h2>
          <p>{isPrescription ? "Track extraction and open the Review Queue to confirm a resident." : "Preview each document and choose whether it is a prescription."}</p>
        </div>
        <Button icon={<RefreshCw size={15} />} loading={loading} onClick={() => load()}>Refresh</Button>
      </div>
      <div className="fax-toolbar">
        <Input aria-label="Search faxes" prefix={<Search size={16} />} placeholder="Search document, fax ID, or sender…" value={query} allowClear onChange={e => setQuery(e.target.value)} />
        <Select aria-label="Filter by status" value={status} onChange={setStatus} options={[
          { value: "all", label: "All statuses" },
          ...(isPrescription ? PRESCRIPTIONS : INCOMING).map(value => ({ value, label: STATUS[value][0] })),
        ]} />
      </div>
      <Table rowKey="id" columns={columns} dataSource={visible} loading={loading}
        scroll={{ x: 1180 }} pagination={{ pageSize: 15, showSizeChanger: false, hideOnSinglePage: true }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error ? "Queue unavailable. Try refreshing." : query || status !== "all" ? "No faxes match your filters." : isPrescription ? "No prescriptions awaiting processing or review." : "No incoming faxes awaiting classification."} /> }} />
      <footer className="fax-panel-footer"><span>{visible.length} of {total} faxes</span>
        <span>{updatedAt ? `Updated ${updatedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · Refreshes every 30 seconds` : "Loading queue…"}</span>
      </footer>
    </div>
    <Modal open={Boolean(preview)} title={preview?.fileName || "Fax document"} width={1000} footer={null}
      onCancel={() => { previewGeneration.current++; setPreview(null); }} destroyOnHidden>
      <div className="fax-preview-meta">From {preview?.fromNumber || "unknown sender"} · {dateTime(preview?.receivedAt || preview?.createdAt)}</div>
      {previewLoading ? <div className="fax-preview-loading">Loading document…</div> : previewError ? (
        <Alert type="error" showIcon title="Document unavailable" description={previewError} />
      ) : preview?.signedUrl && <>
        <p><a href={preview.signedUrl} target="_blank" rel="noopener noreferrer">Open document in a new tab</a></p>
        {preview.contentType?.startsWith("image/") ? (
          <img className="fax-preview-image" src={preview.signedUrl} alt={preview.fileName || "Fax document"}
            onError={() => setPreviewError("The document could not be loaded. Close and reopen the preview to refresh its link.")} />
        ) : <iframe className="fax-preview-frame" src={preview.signedUrl} title={preview.fileName || "Fax document"} />}
      </>}
    </Modal>
  </div>;
}
